import { defineStore } from "pinia";
import { AccountInfo } from "@azure/msal-browser";
import {
  UpdateUserRequest,
  User,
  type UserPermission,
  UserRole,
} from "@/services/api/sdk";
import { computed, ref } from "vue";
import { getInitialsFromName } from "@/utils/string.ts";
import { useApi } from "@/services/api";
import { useAuthentication } from "@/services/authentication";

export interface IOrderFilters {
  text?: string;
  status?: string;
  statusOptions: string[];
}

export const useAuthStore = defineStore(
  "auth",
  () => {
    const auth = useAuthentication();
    const agent = ref<User | null>(null);
    const authedUser = ref<AccountInfo | null>(null);
    const orderFilters = ref<IOrderFilters>({
      statusOptions: [],
    });
    const roleOverride = ref<UserRole | null>(null);
    const isDemoModeEnabled = ref<boolean>(false);
    const newPlayerEnabled = ref<boolean>(true);
    const initialRole = ref<UserRole | null>(null);

    const api = useApi();

    async function fetchActiveUser() {
      if (agent.value) return agent.value;
      agent.value = await api.getCurrentUser();

      if (!initialRole.value) {
        initialRole.value = agent.value.role!;
      }
    }

    async function updateActiveUser(updateUserRequest: UpdateUserRequest) {
      try {
        await api.updateUser({
          userId: agent.value?.id!,
          updateUserRequest,
        });
      } catch {
        throw new Error("error updating current user");
      } finally {
        agent.value = {
          ...agent.value,
          ...updateUserRequest,
        };
      }
    }

    async function setUser(user: AccountInfo | null) {
      authedUser.value = user;
    }

    const isAuthenticated = computed<boolean>(() => {
      return !!authedUser.value;
    });

    const currentUser = computed<string | undefined>(() => {
      return authedUser.value?.name;
    });

    const currentUserInitials = computed<string>(() => {
      if (!authedUser.value?.name) return "";
      return getInitialsFromName(authedUser.value?.name);
    });

    const fullName = computed<string>(() => {
      if (agent.value) {
        return [agent.value.firstName, agent.value.lastName].join(" ");
      }
      return authedUser.value?.name || "";
    });

    const role = computed<UserRole | null>(() => {
      return agent.value?.role ?? null;
    });

    const permissions = computed<UserPermission[] | null>(() => {
      return agent.value?.permissions ?? null;
    });

    const bearerToken = computed<string | undefined>(() => {
      return authedUser.value?.idToken;
    });

    const isAdminUser = computed<boolean>(() => {
      if (!agent.value?.role) return false;
      return (
        [
          UserRole.JackBurtonAdmin,
          UserRole.Admin,
        ] as (typeof UserRole)[keyof typeof UserRole][]
      ).includes(agent.value?.role);
    });

    async function logout() {
      await auth.clearCache();
      await setUser(null);
      localStorage.clear();
      window.location.href = window.location.origin;
    }

    return {
      agent,
      authedUser,
      fetchActiveUser,
      updateActiveUser,
      orderFilters,
      setUser,
      isAdminUser,
      isAuthenticated,
      currentUser,
      currentUserInitials,
      fullName,
      role,
      permissions,
      bearerToken,
      roleOverride,
      isDemoModeEnabled,
      newPlayerEnabled,
      initialRole,
      logout,
    };
  },
  {
    persist: {
      storage: sessionStorage,
      paths: ["roleOverride", "initialRole", "isDemoModeEnabled", "newPlayerEnabled"],
    },
  },
);
